import axios from "axios";
import React, { useEffect, useState, useMemo } from "react";
import { set, useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import AddCompanyInfoModal from "./AddCompanyInfoModal";

function CompensationAdd({ candidate_id, onClose }) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    setError,
    clearErrors,
  } = useForm();

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
  const { setGlobalLoading, token } = useOutletContext();
  const [compData, setCompData] = useState([]);
  const [Esops, setEsops] = useState(false);
  const [HasData, setHasData] = useState(false);
  const total = parseFloat(watch("approx_ctc") || 0);
  const fixed = parseFloat(watch("fixed") || 0);
  const variable = parseFloat(watch("variable") || 0);
  const esops = watch("esops");
  const esopsValue = parseFloat(watch("esops_val") || 0);
  const CURRENCY_API_KEY = process.env.REACT_APP_CURRENCY_API_KEY;
  const [ShowCompanyInfoModal, setShowCompanyInfoModal] = useState(false);
  // const totalFields = fixed + variable + (esops == 1 ? esopsValue : 0);
  const totalFields = fixed + variable;
  const currencyId = watch("currency_id");
  const approxCtc = watch("approx_ctc");
  const currencyType = watch("currency_type_total");
  const currencyTypeFixed = watch("currency_type_fixed");
  const currencyTypeVariable = watch("currency_type_variable");
  const selectedCurrencyObj = compData?.currencies?.find(
    (c) => c.id === parseInt(currencyId),
  );

  const [previousCurrencyName, setPreviousCurrencyName] = useState(null);

  const currencyName = selectedCurrencyObj?.name;
  const currencySymbol = selectedCurrencyObj?.symbol;

  useEffect(() => {
    clearErrors(["approx_ctc", "fixed", "variable", "esops_val"]);

    if (total && totalFields > total) {
      setError("approx_ctc", {
        type: "manual",
        message: "Approx CTC cannot be less than the sum of all fields",
      });
    } else if (total && totalFields < total && totalFields > 0) {
      // Valid scenario, no error
      clearErrors(["approx_ctc"]);
    } else if (!total && totalFields > 0) {
      // Valid: approx_ctc blank
      clearErrors(["approx_ctc"]);
    } else if (total && totalFields === 0) {
      // Valid: fields empty, approx_ctc filled
      clearErrors(["approx_ctc"]);
    }
  }, [total, fixed, variable, esopsValue, esops, clearErrors, setError]);

  useEffect(() => {
    if (esops == 1) setEsops(true);
    else setEsops(false);
  }, [esops]);

  useEffect(() => {
    if (!selectedCurrencyObj) return;

    const currentCurrencyName = selectedCurrencyObj.name;

    if (!previousCurrencyName) {
      setPreviousCurrencyName(currentCurrencyName);
      return;
    }

    const wasINR = previousCurrencyName === "INR";
    const isINR = currentCurrencyName === "INR";

    // Currency group changed (INR <-> Other)
    if (wasINR !== isINR) {
      const defaultType = "None";

      setValue("currency_type_total", defaultType, {
        shouldDirty: true,
      });

      setValue("currency_type_fixed", defaultType, {
        shouldDirty: true,
      });

      setValue("currency_type_variable", defaultType, {
        shouldDirty: true,
      });
    }

    setPreviousCurrencyName(currentCurrencyName);
  }, [selectedCurrencyObj, setValue]);

  useEffect(() => {
    const f = parseFloat(fixed) || 0;
    const v = parseFloat(variable) || 0;

    // calculate sum
    const sum = (f + v).toFixed(1);

    // update approx_ctc ONLY if it differs
    if (sum !== approxCtc) {
      setValue("approx_ctc", sum, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }
  }, [fixed, variable]);

  // useEffect(() => {
  //   if (!currencyTypeFixed || currencyTypeFixed === "None") {
  //     setValue("currency_type_total", "None");
  //   } else {
  //     setValue("currency_type_total", currencyTypeFixed);
  //   }
  // }, [currencyTypeFixed, setValue]);

  const syncCurrencyType = (value) => {
    setValue("currency_type_total", value, { shouldDirty: true });
    setValue("currency_type_fixed", value, { shouldDirty: true });
    setValue("currency_type_variable", value, { shouldDirty: true });
  };

  async function get_compensation() {
    setGlobalLoading(true);
    await axios
      .get(`${API_BASE_URL}/candidates/current_compensation/${candidate_id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        setCompData(response.data);
        setValue("compData", response.data.compensation[0]?.name);
        setValue(
          "companyinfo_id",
          response.data.compensation[0]?.companyinfo_id,
        );
        setValue("fixed", response.data.compensation[0]?.fixed);
        setValue("variable", response.data.compensation[0]?.variable);
        setValue("others", response.data.compensation[0]?.others);
        setValue("esops", response.data.compensation[0]?.esops);
        setValue("esops_val", response.data.compensation[0]?.esops_val);
        setValue("approx_ctc", response.data.compensation[0]?.approx_ctc);
        setValue("comp_id", response.data.compensation[0]?.comp_id);
        response.data.currencies.map((currency) => {
          if (currency.id == response.data.compensation[0]?.currency_id) {
            setValue("currency_id", currency.id);
          }
        });
        if (response.data.compensation[0]?.comp_id != null) setHasData(true);
        setGlobalLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setGlobalLoading(false);
      });
  }

  useEffect(() => {
    get_compensation();
  }, [candidate_id]);

  const getConversation = async (name) => {
    try {
      const response = await axios.get(
        `https://api.currconv.com/api/v7/convert?q=${name}_INR&compact=ultra&apiKey=${CURRENCY_API_KEY}`,
      );
      const rate = response.data;
      const [currency, value] = Object.entries(rate)[0];

      const roundValue = value ? parseFloat(value.toFixed(2)) : 1;
      setValue("conversion_rate", roundValue || 1);
      setValue("approx_conversion_rate", roundValue || 1);
    } catch (error) {
      console.log(error);
    } finally {
    }
  };

  const getMultiplier = (currencyName, type) => {
    if (!type || type === "None") return 1;

    if (currencyName === "INR") {
      if (type === "Lakhs") return 100000;
      if (type === "Crore") return 10000000;
    } else {
      if (type === "Thousand") return 1000;
      if (type === "Million") return 1000000;
    }
    return 1;
  };

  const formatAmount = (value, currencyName, symbol) => {
    if (!value) return "";

    return new Intl.NumberFormat(currencyName === "INR" ? "en-IN" : "en-US", {
      maximumFractionDigits: 0,
    })
      .format(value)
      .replace(/^/, symbol);
  };

  const fullValue = useMemo(() => {
    if (!approxCtc || !selectedCurrencyObj) return "";
    const multiplier = getMultiplier(selectedCurrencyObj.name, currencyType);
    const total = Number(approxCtc) * multiplier;
    return formatAmount(
      total,
      selectedCurrencyObj.name,
      selectedCurrencyObj.symbol,
    );
  }, [approxCtc, currencyType, selectedCurrencyObj]);

  const fixedFullValue = useMemo(() => {
    if (!fixed || !selectedCurrencyObj) return "";

    const multiplier = getMultiplier(
      selectedCurrencyObj.name,
      currencyTypeFixed,
    );

    return formatAmount(
      Number(fixed) * multiplier,
      selectedCurrencyObj.name,
      selectedCurrencyObj.symbol,
    );
  }, [fixed, currencyTypeFixed, selectedCurrencyObj]);

  const variableFullValue = useMemo(() => {
    if (!variable || !selectedCurrencyObj) return "";

    const multiplier = getMultiplier(
      selectedCurrencyObj.name,
      currencyTypeVariable,
    );

    return formatAmount(
      Number(variable) * multiplier,
      selectedCurrencyObj.name,
      selectedCurrencyObj.symbol,
    );
  }, [variable, currencyTypeVariable, selectedCurrencyObj]);

  const onSubmit = (data) => {
    const approx_ctc = parseFloat(data.approx_ctc || 0);
    const fixed = parseFloat(data.fixed || 0);
    const variable = parseFloat(data.variable || 0);
    const esops_val = parseFloat(data.esops_val || 0);
    const esops = data.esops;

    // const totalFields = fixed + variable + (esops == 1 ? esops_val : 0);
    const totalFields = fixed + variable;

    // ✅ Custom validation before submitting
    if (approx_ctc && totalFields > approx_ctc) {
      setError("approx_ctc", {
        type: "manual",
        message: "Approx CTC cannot be less than the sum of all fields.",
      });
      return; //
    }

    // if (approx_ctc && totalFields === 0) {
    //   setError("fixed", {
    //     type: "manual",
    //     message: "Enter at least one value for Fixed, Variable, or Esops",
    //   });
    //   return; //
    // }
    data = {
      ...data,
      candidate_id: candidate_id,
    };
    setGlobalLoading(true);
    console.log(data);
    axios
      .post(`${API_BASE_URL}/candidates/add_compensation`, data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        console.log(response.data);
        setGlobalLoading(false);
        onClose(1);
      })
      .catch((error) => {
        console.log(error);
        setGlobalLoading(false);
      })
      .finally(() => {
        setGlobalLoading(false);
      });
  };

  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            {ShowCompanyInfoModal && (
              <AddCompanyInfoModal
                onClose={(status) => {
                  if (status == 1) {
                    get_compensation();
                  }
                  setShowCompanyInfoModal(false);
                }}
                candidateId={candidate_id}
              />
            )}
            <form onSubmit={handleSubmit(onSubmit)}>
              <div className="modal-header">
                <span className="d-flex" style={{ flexDirection: "column" }}>
                  <h4 className="modal-title">
                    {" "}
                    {HasData ? "Update" : "Add"} Compensation
                  </h4>
                  {
                    <i
                      style={{ fontWeight: "400" }}
                      className="text-info fs-10"
                    >
                      Either Add Total Or Fixed & Variable Compensation
                    </i>
                  }
                </span>
                <button
                  type="button"
                  onClick={() => onClose()}
                  className="btn-close custom-btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <i className="ti ti-x"></i>
                </button>
              </div>
              <div className="modal-body">
                <div className="row">
                  <div className="col-xxl-12 col-lg-12 col-md-12">
                    <div className="card">
                      <div className="card-body my-card-body">
                        <div className="row">
                          {compData?.compensation?.length <= 0 ? (
                            <span className="text-danger fs-12">
                              No Experience Found. Please Add Experience -
                              <a
                                onClick={() => setShowCompanyInfoModal(true)}
                                className="btn btn-sm btn-primary fs-8"
                              >
                                Add Experience
                              </a>
                            </span>
                          ) : (
                            <>
                              <input type="hidden" {...register("comp_id")} />
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                <div className="form-group">
                                  <label className="form-label">
                                    Current Company
                                  </label>
                                  <input
                                    type="hidden"
                                    {...register("companyinfo_id")}
                                  />
                                  <input
                                    {...register("compData", {
                                      required: "Required",
                                    })}
                                    type="text"
                                    className="form-control fs-10"
                                    readOnly={true}
                                  />
                                  {errors.companyinfo_id && (
                                    <span className="text-danger">
                                      {errors.companyinfo_id.message}
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                <div className="form-group">
                                  <label className="form-label">
                                    Currency{" "}
                                    {errors.currency_id && (
                                      <span className="text-danger fs-10">
                                        {errors.currency_id.message}
                                      </span>
                                    )}
                                  </label>
                                  <select
                                    {...register("currency_id", {
                                      required: "Required",
                                      onChange: (e) => {
                                        const selectedId = parseInt(
                                          e.target.value,
                                        ); // selected value is the currency id
                                        const selectedCurrency =
                                          compData?.currencies?.find(
                                            (c) => c.id === selectedId,
                                          );

                                        if (selectedCurrency) {
                                          getConversation(
                                            selectedCurrency.name,
                                          );
                                        }
                                      },
                                    })}
                                    className="form-select"
                                  >
                                    <option value={""}>Select Currency</option>
                                    {compData?.currencies?.map((currency) => (
                                      <option
                                        key={currency.id}
                                        value={currency.id}
                                      >{`${currency.name} - ${currency.symbol}`}</option>
                                    ))}
                                  </select>
                                </div>
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="form-group">
                                  <label className="form-label">
                                    Total (Fix+Var)
                                    <span className="ms-1 fs-10">
                                      {errors.approx_ctc && (
                                        <span className="text-danger">
                                          {errors.approx_ctc.message}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  <input
                                    {...register("approx_ctc", {
                                      required: !fixed ? "Required" : false,
                                      pattern: {
                                        value: /^\d+(\.\d{0,2})?$/,
                                        message:
                                          "Only numbers allowed with up to 2 decimals",
                                      },
                                    })}
                                    type="text"
                                    className="form-control fs-10"
                                    autoComplete="off"
                                    onInput={(e) => {
                                      // remove invalid characters except decimal point
                                      e.target.value = e.target.value.replace(
                                        /[^0-9.]/g,
                                        "",
                                      );
                                      // remove extra decimals
                                      const parts = e.target.value.split(".");
                                      if (parts[1]?.length > 2) {
                                        e.target.value =
                                          parts[0] + "." + parts[1].slice(0, 2);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="form-group">
                                  <label className="form-label"></label>
                                  <select
                                    {...register("currency_type_total")}
                                    className="form-select"
                                    onChange={(e) =>
                                      syncCurrencyType(e.target.value)
                                    }
                                  >
                                    <option value={"None"}>None</option>
                                    {selectedCurrencyObj?.name === "INR" ? (
                                      <>
                                        <option value="Crore">Crore</option>
                                        <option value="Lakhs">Lakhs</option>
                                      </>
                                    ) : selectedCurrencyObj ? (
                                      <>
                                        <option value="Million">Million</option>
                                        <option value="Thousand">
                                          Thousand
                                        </option>
                                      </>
                                    ) : null}
                                  </select>
                                </div>
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="form-group">
                                  <label className="form-label">
                                    Fixed
                                    <span className="ms-1 fs-10">
                                      {errors.fixed && (
                                        <span className="text-danger">
                                          {errors.fixed.message}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  <input
                                    {...register("fixed", {
                                      required: !total ? "Required" : false,
                                      pattern: {
                                        value: /^\d+(\.\d{0,2})?$/,
                                        message:
                                          "Only numbers allowed with up to 2 decimals",
                                      },
                                    })}
                                    type="text"
                                    className="form-control fs-10"
                                    autoComplete="off"
                                    onInput={(e) => {
                                      // remove invalid characters except decimal point
                                      e.target.value = e.target.value.replace(
                                        /[^0-9.]/g,
                                        "",
                                      );
                                      // remove extra decimals
                                      const parts = e.target.value.split(".");
                                      if (parts[1]?.length > 2) {
                                        e.target.value =
                                          parts[0] + "." + parts[1].slice(0, 2);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="form-group">
                                  <label className="form-label"></label>
                                  <select
                                    {...register("currency_type_fixed")}
                                    className="form-select"
                                    onChange={(e) =>
                                      syncCurrencyType(e.target.value)
                                    }
                                  >
                                    <option value={"None"}>None</option>
                                    {selectedCurrencyObj?.name === "INR" ? (
                                      <>
                                        <option value="Crore">Crore</option>
                                        <option value="Lakhs">Lakhs</option>
                                      </>
                                    ) : selectedCurrencyObj ? (
                                      <>
                                        <option value="Million">Million</option>
                                        <option value="Thousand">
                                          Thousand
                                        </option>
                                      </>
                                    ) : null}
                                  </select>
                                </div>
                              </div>
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                <label className="form-label">
                                  {fullValue && (
                                    <div className="fs-12 text-muted mt-1">
                                      Full value:{" "}
                                      <span className="text-info">
                                        {fullValue}
                                      </span>
                                    </div>
                                  )}
                                </label>
                              </div>
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                {fixedFullValue && (
                                  <div className="fs-12 text-muted mt-1">
                                    Full value:{" "}
                                    <span className="text-info">
                                      {fixedFullValue}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="fixed_ctc">
                                  <label className="form-label">
                                    Variable
                                    <span className="ms-1 fs-10">
                                      {errors.variable && (
                                        <span className="text-danger">
                                          {errors.variable.message}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  <input
                                    {...register("variable", {
                                      required: !total ? "Required" : false,
                                      pattern: {
                                        value: /^\d+(\.\d{0,2})?$/,
                                        message:
                                          "Only numbers allowed with up to 2 decimals",
                                      },
                                    })}
                                    type="text"
                                    className="form-control fs-10"
                                    autoComplete="off"
                                    onInput={(e) => {
                                      // remove invalid characters except decimal point
                                      e.target.value = e.target.value.replace(
                                        /[^0-9.]/g,
                                        "",
                                      );
                                      // remove extra decimals
                                      const parts = e.target.value.split(".");
                                      if (parts[1]?.length > 2) {
                                        e.target.value =
                                          parts[0] + "." + parts[1].slice(0, 2);
                                      }
                                    }}
                                  />
                                </div>
                              </div>
                              <div className="col-xxl-3 col-lg-3 col-md-3">
                                <div className="form-group">
                                  <label className="form-label"></label>
                                  <select
                                    {...register("currency_type_variable")}
                                    className="form-select"
                                    onChange={(e) =>
                                      syncCurrencyType(e.target.value)
                                    }
                                  >
                                    <option value={"None"}>None</option>
                                    {selectedCurrencyObj?.name === "INR" ? (
                                      <>
                                        <option value="Crore">Crore</option>
                                        <option value="Lakhs">Lakhs</option>
                                      </>
                                    ) : selectedCurrencyObj ? (
                                      <>
                                        <option value="Million">Million</option>
                                        <option value="Thousand">
                                          Thousand
                                        </option>
                                      </>
                                    ) : null}
                                  </select>
                                </div>
                              </div>
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                <div className="fixed_ctc">
                                  <label className="form-label">
                                    Other Variable
                                    <span className="ms-1 fs-10">
                                      {errors.others && (
                                        <span className="text-danger">
                                          {errors.others.message}
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  <input
                                    {...register("others")}
                                    type="text"
                                    className="form-control fs-10"
                                    autoComplete="off"
                                  />
                                </div>
                              </div>
                              <div className="col-xxl-12 col-lg-12 col-md-12">
                                {variableFullValue && (
                                  <div className="fs-12 text-muted mt-1">
                                    Full value:{" "}
                                    <span className="text-info">
                                      {variableFullValue}
                                    </span>
                                  </div>
                                )}
                              </div>
                              <div className="col-xxl-6 col-lg-6 col-md-6">
                                <div className="form-group">
                                  <label className="form-label">
                                    ESOPs/ RSUs
                                  </label>
                                  <select
                                    {...register("esops")}
                                    className="form-select"
                                    defaultValue={0}
                                  >
                                    <option value={""}>Select</option>
                                    <option value={1}>Yes</option>
                                    <option value={0}>No</option>
                                  </select>
                                </div>
                              </div>
                              {Esops && (
                                <div className="col-xxl-6 col-lg-6 col-md-6">
                                  <div className="form-group">
                                    <label className="form-label">
                                      ESOPs/ RSUs Value
                                      <span className="ms-1 fs-10">
                                        {errors.esops_val && (
                                          <span className="text-danger">
                                            {errors.esops_val.message}
                                          </span>
                                        )}
                                      </span>
                                    </label>
                                    <input
                                      {...register("esops_val", {
                                        required: "Required",
                                        pattern: {
                                          value: /^\d+(\.\d{0,2})?$/,
                                          message:
                                            "Only numbers allowed with up to 2 decimals",
                                        },
                                      })}
                                      type="text"
                                      className="form-control fs-10"
                                      autoComplete="off"
                                      onInput={(e) => {
                                        // remove invalid characters except decimal point
                                        e.target.value = e.target.value.replace(
                                          /[^0-9.]/g,
                                          "",
                                        );
                                        // remove extra decimals
                                        const parts = e.target.value.split(".");
                                        if (parts[1]?.length > 2) {
                                          e.target.value =
                                            parts[0] +
                                            "." +
                                            parts[1].slice(0, 2);
                                        }
                                      }}
                                    />
                                  </div>
                                </div>
                              )}
                              {/* ===== Summary Section ===== */}
                              {(fullValue ||
                                fixedFullValue ||
                                variableFullValue) && (
                                <div className="mt-4">
                                  <div className="border rounded p-3 bg-light">
                                    <h6 className="mb-3 fw-semibold">
                                      Summary{" "}
                                      <span className="text-muted">
                                        (Full Values)
                                      </span>
                                    </h6>

                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Total:</span>
                                      <span className="text-primary fw-semibold">
                                        {fullValue || "-"}
                                      </span>
                                    </div>

                                    <div className="d-flex justify-content-between mb-2">
                                      <span className="text-muted">Fixed:</span>
                                      <span className="text-primary fw-semibold">
                                        {fixedFullValue || "-"}
                                      </span>
                                    </div>

                                    <div className="d-flex justify-content-between">
                                      <span className="text-muted">
                                        Variable:
                                      </span>
                                      <span className="text-primary fw-semibold">
                                        {variableFullValue || "-"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="submit" className="btn btn-primary mx-1">
                  Save
                </button>
                <button
                  type="button"
                  onClick={() => onClose()}
                  className="btn btn-secondary"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  Close
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

export default CompensationAdd;
